// The landing-page hero simulation (design-system §7 / PRD 012 §6): a canned
// brainstorm that replays on a loop — speaker bubbles land, then the AI's typed
// cards animate inline (a brief "thinking" state, then the contribution fills).
// It is the product's own demo: no STT, no reflex, no credits — a scripted timer
// driving the *real* card language (aiTurnStyle) so the landing looks exactly like
// a live session. Respects prefers-reduced-motion (renders the whole thread at
// rest, no loop).

import { Loader2, Sparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { ContactAvatar } from '@/components/shell/ContactAvatar'
import { KIND_STYLE, TONE } from '@/components/shell/aiTurnStyle'
import { cn } from '@/lib/utils'
import type { AiTurnKind } from '@/lib/ai/types'

interface SimSpeaker {
  name: string
  color: string
}

type SimItem =
  | { type: 'speech'; speaker: SimSpeaker; text: string }
  | {
    type: 'ai'
    kind: AiTurnKind
    text: string
    detail?: string
    thinking: string
  }

const MARA: SimSpeaker = { name: 'Mara', color: 'text-accent' }
const DEV: SimSpeaker = { name: 'Dev', color: 'text-success' }

// The scripted thread — a short product brainstorm that walks the four beats
// (talk → live transcript → AI participates → shared whiteboard).
const SCRIPT: SimItem[] = [
  {
    type: 'speech',
    speaker: MARA,
    text: 'The core idea is a thinking partner that actually follows the conversation — not a bot you prompt.'
  },
  {
    type: 'speech',
    speaker: DEV,
    text: 'Right. It should only speak when it genuinely has something. Otherwise it just listens and keeps the thread.'
  },
  {
    type: 'ai',
    kind: 'insight',
    text: 'You two are circling the same constraint — the value is that it never interrupts.',
    detail: 'Reframes “assistant” as “participant”.',
    thinking: 'Connecting the last two turns…'
  },
  {
    type: 'speech',
    speaker: MARA,
    text: 'Love that. What if the room could pull it in on demand though?'
  },
  {
    type: 'ai',
    kind: 'candidate',
    text: 'Let the room address it by name — an explicit opt-in when you actually want its take.',
    detail: 'Direct address, no always-on chatter.',
    thinking: 'Forming an idea…'
  },
  {
    type: 'speech',
    speaker: DEV,
    text: 'And what’s the risk if it talks too much?'
  },
  {
    type: 'ai',
    kind: 'caution',
    text: 'Over-contributing breaks the ambient promise — cap interjections and bias hard toward silence.',
    thinking: 'Weighing the risk…'
  },
  {
    type: 'speech',
    speaker: MARA,
    text: 'Everything it surfaces should land on the shared whiteboard, too.'
  },
  {
    type: 'ai',
    kind: 'present',
    text: 'On it — I’ll pin the three constraints you just set straight to the board as we go.',
    thinking: 'Drafting a reply…'
  }
]

const REVEAL_MS = 1600 // gap between thread items
const THINK_MS = 1100 // how long an AI card shows its "thinking" state
const RESTART_MS = 4200 // pause on a full thread before looping

function SpeechBubble({
  speaker,
  text
}: {
  speaker: SimSpeaker
  text: string
}) {
  return (
    <div className="flex gap-2.5">
      <ContactAvatar
        name={speaker.name}
        color={speaker.color}
        avatar={null}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <div className={cn('mb-0.5 text-label font-semibold', speaker.color)}>
          {speaker.name}
        </div>
        <div className="inline-block max-w-full rounded-2xl rounded-tl-sm border border-border bg-surface px-3 py-2 text-body leading-relaxed text-text">
          {text}
        </div>
      </div>
    </div>
  )
}

function AiPresent({
  text,
  thinking,
  pending
}: {
  text: string
  thinking: string
  pending: boolean
}) {
  return (
    <div className="flex gap-2.5">
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
        <Sparkles className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 text-label font-semibold text-accent">
          AI participant
        </div>
        <div className="inline-block max-w-full rounded-2xl rounded-tl-sm border border-accent/40 bg-accent/5 px-3 py-2 text-body leading-relaxed text-text">
          {pending ? (
            <span className="flex items-center gap-2 text-muted-foreground italic">
              <Loader2 className="size-3.5 shrink-0 animate-spin" />
              {thinking}
            </span>
          ) : (
            text
          )}
        </div>
      </div>
    </div>
  )
}

function AiCard({
  kind,
  text,
  detail,
  thinking,
  pending
}: {
  kind: AiTurnKind
  text: string
  detail?: string
  thinking: string
  pending: boolean
}) {
  const style = KIND_STYLE[kind]
  const t = TONE[style.tone]
  const Icon = style.Icon
  return (
    <div className="flex gap-2.5">
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
        <Sparkles className="size-3.5" />
      </span>
      <div
        className={cn(
          'min-w-0 flex-1 rounded-lg border border-l-2 px-2.5 py-2',
          t.border,
          t.bg
        )}
      >
        <div
          className={cn(
            'flex items-center gap-1.5 text-label font-semibold tracking-wide uppercase',
            t.text
          )}
        >
          <Icon className="size-3.5 shrink-0" />
          <span className="truncate">{style.label}</span>
        </div>
        {pending ? (
          <p className="mt-1 flex items-center gap-2 text-body-sm text-muted-foreground italic">
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
            {thinking}
          </p>
        ) : (
          <>
            <p className="mt-1 text-body-sm text-text">{text}</p>
            {detail && (
              <p className="mt-0.5 text-label text-muted-foreground">
                ↳ {detail}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function renderItem(item: SimItem, pending: boolean) {
  if (item.type === 'speech')
    return <SpeechBubble speaker={item.speaker} text={item.text} />
  if (item.kind === 'present')
    return (
      <AiPresent text={item.text} thinking={item.thinking} pending={pending} />
    )
  return (
    <AiCard
      kind={item.kind}
      text={item.text}
      detail={item.detail}
      thinking={item.thinking}
      pending={pending}
    />
  )
}

export function LandingSim() {
  // `count` items are mounted; the last one is "pending" while `pending` is true.
  const [count, setCount] = useState(0)
  const [pending, setPending] = useState(false)
  const reduced = useRef(false)

  useEffect(() => {
    reduced.current =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    // Reduced motion: show the whole thread at rest, no loop.
    if (reduced.current) {
      setCount(SCRIPT.length)
      setPending(false)
      return
    }

    let timer: ReturnType<typeof setTimeout>
    // Drive the loop off `count`/`pending` transitions via a self-scheduling step.
    function step(shown: number) {
      if (shown >= SCRIPT.length) {
        timer = setTimeout(() => {
          setCount(0)
          setPending(false)
          step(0)
        }, RESTART_MS)
        return
      }
      const next = SCRIPT[shown]
      if (!next) return
      setCount(shown + 1)
      if (next.type === 'ai') {
        setPending(true)
        timer = setTimeout(() => {
          setPending(false)
          timer = setTimeout(() => step(shown + 1), REVEAL_MS)
        }, THINK_MS)
      } else {
        setPending(false)
        timer = setTimeout(() => step(shown + 1), REVEAL_MS)
      }
    }
    timer = setTimeout(() => step(0), 500)
    return () => clearTimeout(timer)
  }, [])

  const items = SCRIPT.slice(0, count)

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-bg shadow-(--shadow-md)">
      {/* A window chrome so it reads as a live app, not a static image. */}
      <div className="flex items-center gap-2 border-b border-border bg-sidebar px-3 py-2">
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-full bg-danger/60" />
          <span className="size-2 rounded-full bg-warn/60" />
          <span className="size-2 rounded-full bg-success/60" />
        </span>
        <span className="ml-1 text-label text-muted-foreground">
          Product brainstorm · live
        </span>
        <span className="ml-auto flex items-center gap-1 text-label text-accent">
          <Sparkles className="size-3.5" />
          AI listening
        </span>
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 py-4">
        {items.map((item, i) => (
          <div
            key={i}
            className="motion-safe:animate-[sim-in_240ms_var(--ease-emphasized)]"
          >
            {renderItem(item, pending && i === count - 1)}
          </div>
        ))}
      </div>
    </div>
  )
}
